import { Component, prop } from "@kluntje/core";
import { autobind } from "Helper/functionHelper/decorators";
import { I18nService } from "Services/I18nService";

import { toDateString, toDateTimeString, toLocaleDateString, toTimeString } from "./dateUtils";

/**
 * Custom Element to be used as part of datetime component, will have similar api / usage as `input[type=date]` and `input[type=datetime-local]`,
 * but the design, UX and some form endpoints didn't want the input element, therefor it is replaced with this custom element.
 * the properties and methods of this custom element aren't meant to be used directly but rather via the wrapping `fra-m-datetime` element.
 * e.g. `querySelector(fra-m-datetime).end` instead of `querySelectorAll(fra-m-datetime__field)[1].value`
 */
class FraDatepickerField extends Component {
  // used to access selected label and value from other components
  static label = "";
  static value = "";

  /**
   * converts date sting with potential special values `"{NOW}"`, `"{NOW+7d}"`, `"{NOW-10d}", `"{TODAY+1d}T23:45"` to the date from current date.
   * use "NOW" when needing the exact hours:mins in combination with `[type=datetime]`, TODAY otherwise.
   *
   * @param {string} val - the date string which might be a special string instead of a formatted date(time) string
   * @param {boolean} [withTime=false] - if `true` will return a date+time (e.g. `"2022-11-03T15:10"`) string, when false / not present a formatted date string (e.g. `"2022-11-03"`) is returned
   * @returns {string} - "YYYY-MM-DD" or "YYYY-MM-DDThh:mm" depending on the withTime argument
   */
  static normalizeDateValue(val: string, withTime = false) {
    // "{NOW}", "{NOW+7d}", "{NOW-12d}", "{TODAY}T23:00"
    const regex = /^\{(?<keyword>NOW|TODAY)((?<operation>[+-])(?<numberOfDays>\d+)d)?}/;
    const result = regex.exec(val);

    if (result === null) return val;

    const date = new Date();
    // has + or - #days
    if (result.groups!.operation) {
      const days = Number(result.groups!.numberOfDays);
      // +
      if (result.groups!.operation === "+") date.setDate(date.getDate() + days);
      // -
      else date.setDate(date.getDate() - days);
    }

    // TODAY
    if (result.groups!.keyword === "TODAY") {
      val = val.replace(regex, toDateString(date));
    }
    // NOW
    else {
      val = val.replace(regex, toDateTimeString(date));
    }

    return withTime ? toDateTimeString(val) : toDateString(val);
  }

  // Assuming that at this point the dictionary takes time to load,
  // we are setting placeholder values for the labels
  // after render, if the dictionary is loaded, translated values are returned

  static timeSlots() {
    const translate = I18nService.getInstance();
    const translateKey = (key: string) => translate.getKey(key);

    return [
      {
        date: FraDatepickerField.normalizeDateValue("{TODAY}"),
        label: translateKey("fraport.travel.components.flightBooking.button.today.label"),
      },
      {
        date: FraDatepickerField.normalizeDateValue("{TODAY+1d}"),
        label: translateKey("fraport.travel.components.flightBooking.button.tomorrow.label"),
      },
      {
        date: FraDatepickerField.normalizeDateValue("{TODAY+2d}"),
        label: translateKey("fraport.travel.components.flightBooking.button.intwodays.label"),
      },
    ];
  }

  /**
   * field will show only a date or a date + a time.
   * similar to the `input[type=date]` and `input[type=datetime-local]`
   * @type {"date" | "datetime"}
   */
  @prop({ required: true, type: "string" })
  type: "date" | "datetime" | undefined;

  /**
   * field will show only a date with flightSearch.
   * @variant {"flightSearch"}
   */
  @prop({ required: false })
  variant: "flightsearch" | undefined;

  /**
   * min allowed date /date+time which the user can choose via the datepicker widget.
   * format the same as in {FraDatepickerField#value}
   * (attribute is optional)
   */
  @prop({ required: false })
  min = "";

  /**
   * max allowed date /date+time which the user can choose via the datepicker widget.
   * format the same as in {FraDatepickerField#value}
   * (attribute is optional)
   */
  @prop({ required: false })
  max = "";

  /**
   * if attribute is set (in the dom or via property), then when a form is submitted / the formData generated,
   * the current `value` will be added by the given attribute value
   * (attribute is optional)
   */
  @prop({ required: false })
  formFieldName = null;

  /**
   * if attribute is set (in the dom or via property), then when a form is submitted / the formData generated,
   * the date part of current `value` will be added by the given attribute value. (should be used in combination with type=datetime)
   * (attribute is optional)
   */
  @prop({ required: false })
  dateFormFieldName = null;

  /**
   * if attribute is set (in the dom or via property), then when a form is submitted / the formData generated,
   * the time part of current `value` will be added by the given attribute value. (should be used in combination with type=datetime)
   * (attribute is optional)
   */
  @prop({ required: false })
  timeFormFieldName = null;

  /**
   * label used for the time part in the calendar dialog
   */
  @prop({ required: true })
  timeLabel = "";

  /**
   * if disabled, the element won't participate in the form submit,
   * and the parent datetime-picker won't use it (e.g. for the start or end date, or won't open if all fields are disabled)
   */
  @prop({ type: "boolean" })
  disabled = false;

  /**
   * reflects the `value` attribute of the element.
   * which returns the current value (date) of the field, in the (ISO8601) format (similar to input date/datetime-local) of
   *  "YYYY-MM-DD" for [type=date]
   *  "YYYY-MM-DDThh:mm" [type=datetime]
   * on page load / when added to the DOM, it also can have placeholders (e.g. {TODAY+5d}) which will be replaced with actual values.
   * see {FraDatepickerField.normalizeDateValue} for more examples
   */
  get value() {
    return this.getAttribute("value") || "";
  }

  set value(val) {
    if (val === undefined || val === null) this.removeAttribute("value");
    else {
      // re-format
      val = this.type === "datetime" ? toDateTimeString(val) : toDateString(val);
      this.setAttribute("value", val);
      FraDatepickerField.value = val;
      this.displayValue = toLocaleDateString(val, this.type === "datetime");
    }
  }

  /**
   * if placed inside an HTMLFormElement,
   * will return a reference to that form element
   * @readonly
   */
  get form() {
    return this.closest("form");
  }

  /**
   * will set the date/datetime value in a localized format for the user to read,
   * it will NOT always reflect the `value`. for example when the user selects some date but hasn't applied it and might cancel its usage.
   * @param {string} value
   */
  set displayValue(value: string) {
    FraDatepickerField.label = value;
    this.innerText = value;
  }

  get displayValue() {
    return this.innerText;
  }

  /**
   * @override
   */
  async afterComponentRender(): Promise<any> {
    await I18nService.getInstance().waitForJson();
    const withTime = this.type === "datetime";
    // convert special values (e.g. "{TODAY+7d}") to real Date values
    const options = ["value", "min", "max"];
    options.forEach(option => {
      if (this[option]) this[option] = FraDatepickerField.normalizeDateValue(this[option]!, withTime);
    });

    // as long as safari doesn't support element internals,
    // use the formdata event to inject necessary form fields,
    // in current use cases, there is always a default/initial value,
    // therefore not being able to participate in the validation phase isn't a problem when not attaching internals
    if (this.form) this.form.addEventListener("formdata", this.handleFormData);

    if (sessionStorage.getItem("restoreFlightOverview")) {
      this.handleSearchRestoreDate(JSON.parse(sessionStorage.getItem("restoreFlightOverview")!));
    }
  }

  handleSearchRestoreDate(restoreObject: any) {
    const date = new Date(restoreObject.formData.time);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    this.value = `${year}-${month}-${day}`;
  }

  /**
   * @override
   */
  beforeComponentDisconnects() {
    if (this.form) this.form.removeEventListener("formdata", this.handleFormData);
  }

  /**
   * inject date/time values into the form data
   * @param {FormDataEvent} param0
   */
  @autobind
  handleFormData({ formData }: FormDataEvent) {
    if (this.disabled) return;

    Object.entries(this.getFormValues()).forEach(([key, value]) => {
      // assuming the names are unique and two fields wont have the same name so no need to add, can replace
      formData.set(key, value);
    });
  }

  getFormValues() {
    const result: { [key: string]: string } = {};

    if (this.timeFormFieldName) result[this.timeFormFieldName] = toTimeString(this.value);
    if (this.dateFormFieldName) result[this.dateFormFieldName] = toDateString(this.value);
    if (this.formFieldName) result[this.formFieldName] = this.value;

    return result;
  }
}

customElements.define("fra-e-datetime-picker__field", FraDatepickerField);

export default FraDatepickerField;
